import { useEffect, useRef } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '@/lib/api'
import { clientDashboardKeys } from './clientDashboards'
import { invalidateRequestViews } from './clientMeetings'
import { extractErrorMessage } from './ticketMutations'
import type { ClientEmail } from '@/types/clientEmails'

export const clientEmailKeys = {
  list: (companyId: string) => [...clientDashboardKeys.all, 'emails', companyId] as const,
  text: (emailId: string) => [...clientDashboardKeys.all, 'email-text', emailId] as const,
}

export function useClientEmails(companyId: string) {
  return useQuery({
    queryKey: clientEmailKeys.list(companyId),
    queryFn: () => api<{ data: ClientEmail[] }>(`/client-dashboards/${companyId}/emails`),
    // Poll while an email is being read, so its requests appear without a reload.
    refetchInterval: query => (query.state.data?.data.some(email => email.requestsStatus === 'pending') ? 5000 : false),
  })
}

// Refresh every view that shows requests once an email that was being read has been read, wherever staff are.
export function useEmailReadsRefresh(companyId: string) {
  const queryClient = useQueryClient()
  const emails = useClientEmails(companyId).data?.data
  const reading = useRef<Set<string>>(new Set())
  useEffect(() => {
    if (!emails) return
    const stillReading = new Set(emails.filter(email => email.requestsStatus === 'pending').map(email => email._id))
    const finished = [...reading.current].some(emailId => !stillReading.has(emailId))
    reading.current = stillReading
    if (finished) void invalidateRequestViews(queryClient)
  }, [emails, queryClient])
}

export function useLogEmail(companyId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: { text: string; subject?: string }) => api<{ data: ClientEmail }>(`/client-dashboards/${companyId}/emails`, { method: 'POST', body }),
    onSuccess: () => {
      toast.success('Email logged. Its requests will appear shortly.')
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: clientEmailKeys.list(companyId) }),
        queryClient.invalidateQueries({ queryKey: [...clientDashboardKeys.all, 'not-logged', companyId] }),
      ])
    },
  })
}

export function useEmailText(emailId: string, enabled: boolean) {
  return useQuery({
    queryKey: clientEmailKeys.text(emailId),
    queryFn: () => api<{ data: { _id: string; text: string } }>(`/client-dashboards/emails/${emailId}`),
    enabled,
  })
}

export function useDeleteEmail() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ emailId }: { emailId: string }) => api(`/client-dashboards/emails/${emailId}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success('Email deleted')
      return invalidateRequestViews(queryClient)
    },
    onError: err => toast.error(extractErrorMessage(err, "Couldn't delete the email")),
  })
}

export function useRetryEmail() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ emailId }: { emailId: string }) => api(`/client-dashboards/emails/${emailId}/requests/retry`, { method: 'POST' }),
    onSuccess: () => {
      toast.success('Reading this email again')
      return invalidateRequestViews(queryClient)
    },
    onError: err => toast.error(extractErrorMessage(err, "Couldn't read the email again")),
  })
}

